import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { AnnualPlan, Area, Activity } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf } from "../../../Shared/pdfCharts";
import { AP_STATUS_LABEL } from "../../../../utils/annualPlanStatus";
import "./AnnualPlanDashboard.scss";

const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
// Gama para las donas de acá (área/frecuencia) — misma que Requisitos.
const DONUT_PALETTE = ["#C4D693", "#A3B76B", "#8CCAFF", "#001936"];
const MONTH_BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const MONTH_BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const MONTH_BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const MONTH_BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1 };
const MONTH_BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };

// Mismos 3 colores (Alto/Medio/Bajo) que la leyenda "Referencias" de
// Comparativa en Inicio / Requisitos.
const LEGEND_SWATCH_COLORS = {
  high: { blue: "#8CCAFF", green: "#B0C676" },
  mid:  { blue: "#BFD8ED", green: "#C8E087" },
  low:  { blue: "#DBEFFF", green: "#DDFCAD" },
};
const LEGEND_STEPS = [
  { key: "high", labelKey: "high" },
  { key: "mid",  labelKey: "medium" },
  { key: "low",  labelKey: "low" },
];
function apHexToRgb(hex) {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}
const LEGEND_SCALE_RGB = {
  blue:  { low: apHexToRgb(LEGEND_SWATCH_COLORS.low.blue),  mid: apHexToRgb(LEGEND_SWATCH_COLORS.mid.blue),  high: apHexToRgb(LEGEND_SWATCH_COLORS.high.blue) },
  green: { low: apHexToRgb(LEGEND_SWATCH_COLORS.low.green), mid: apHexToRgb(LEGEND_SWATCH_COLORS.mid.green), high: apHexToRgb(LEGEND_SWATCH_COLORS.high.green) },
};
function legendPillStyle(val, max = 100, tone = "green") {
  const ratio = max > 0 ? Math.min(1, Math.max(0, val / max)) : 0;
  const { low, mid, high } = LEGEND_SCALE_RGB[tone];
  const [r, g, b] = ratio <= 0.5
    ? low.map((c, i) => Math.round(c + (mid[i] - c) * (ratio / 0.5)))
    : mid.map((c, i) => Math.round(c + (high[i] - c) * ((ratio - 0.5) / 0.5)));
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return { backgroundColor: `rgb(${r},${g},${b})`, color: brightness < 150 ? "#fff" : "#1a1c40" };
}

// Mismo mapeo de tonos que Requisitos: no iniciado≈pendiente,
// vencido≈no cumple, realizado≈cumple, en progreso≈parcial.
const STATUS_PILL_COLORS = {
  pending:     { bg: "#001936", color: "#fff" },
  overdue:     { bg: "#8CCAFF", color: "#1a1c40" },
  completed:   { bg: "#A3B76B", color: "#1a1c40" },
  in_progress: { bg: "#C4D693", color: "#1a1c40" },
};

function formatDate(date) {
  if (!date) return "-";
  const str = typeof date === "string" ? date : date.toISOString();
  const [y, m, d] = str.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

function ReadOnlyTable({ items, t, showSite }) {
   const [sortCol, setSortCol] = React.useState(null);
    const [sortDir, setSortDir] = React.useState("ascending");
  
    const handleSort = (col) => {
      if (sortCol === col) {
        setSortDir((d) => d === "ascending" ? "descending" : "ascending");
      } else {
        setSortCol(col);
        setSortDir("ascending");
      }
    };
  
    const sorted = React.useMemo(() => {
      if (!sortCol) return items;
      return [...items].sort((a, b) => {
        let va = "", vb = "";
        if (sortCol === "area")   { va = a.area?.name   || ""; vb = b.area?.name   || ""; }
        if (sortCol === "status") { va = a.status        || ""; vb = b.status        || ""; }
        const cmp = va.localeCompare(vb);
        return sortDir === "ascending" ? cmp : -cmp;
      });
    }, [items, sortCol, sortDir]);
  
    const thProps = (col) => ({
      onClick: () => handleSort(col),
      style: { cursor: "pointer", userSelect: "none" },
    });
  
    if (!items.length) {
      return <p style={{ textAlign: "center", color: "#999", padding: "1.5rem 0" }}>{t("no_results")}</p>;
    }
  
  return (
    <div className="ap-dashboard__ro-table-wrapper">
      <Table className="ap-dashboard__ro-table unstackable rtable-cards">
        <Table.Header>
          <Table.Row>
            {showSite && <Table.HeaderCell>{t("site")}</Table.HeaderCell>}
            <Table.HeaderCell {...thProps("area")}>{t("area")}</Table.HeaderCell>
            <Table.HeaderCell>{t("activity")}</Table.HeaderCell>
            <Table.HeaderCell>{t("frequency")}</Table.HeaderCell>
            <Table.HeaderCell>{t("scheduled_date")}</Table.HeaderCell>
            <Table.HeaderCell {...thProps("status")}>{t("status")}</Table.HeaderCell>
            <Table.HeaderCell>{t("progress")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sorted.map((item) => (
            <Table.Row key={item._id}>
              {showSite && <Table.Cell data-label={t("site")}>{item.site?.name || "-"}</Table.Cell>}
              <Table.Cell data-label={t("area")}>{item.area?.name        || "-"}</Table.Cell>
              <Table.Cell data-label={t("activity")}>{item.actividades?.name || "-"}</Table.Cell>
              <Table.Cell data-label={t("frequency")}>{item.frecuencia?.name  || "-"}</Table.Cell>
              <Table.Cell data-label={t("scheduled_date")}>
                {item.occurrences?.length > 1
                  ? <span style={{ fontSize: "0.82em", whiteSpace: "nowrap" }}>
                      {formatDate(item.occurrences[0].date)} → {formatDate(item.occurrences[item.occurrences.length - 1].date)}
                    </span>
                  : formatDate(item.occurrences?.[0]?.date || item.fecha_programada)}
              </Table.Cell>
              <Table.Cell data-label={t("status")}>
                {item.status
                  ? (() => {
                      const pill = STATUS_PILL_COLORS[item.status] || { bg: "#E4E4E4", color: "#1a1c40" };
                      return (
                        <span className="ap-dashboard__row-pill" style={{ backgroundColor: pill.bg, color: pill.color }}>
                          {AP_STATUS_LABEL[item.status] || item.status}
                        </span>
                      );
                    })()
                  : "-"}
              </Table.Cell>
              <Table.Cell data-label={t("progress")}>
                <span className="ap-dashboard__row-pill" style={legendPillStyle(item.progress || 0, 100, "green")}>
                  {item.progress || 0}%
                </span>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

const apController       = new AnnualPlan();
const areaController     = new Area();
const activityController = new Activity();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});

const MONTH_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

function EmptyMsg({ text = "Sin datos" }) {
  return <div className="ap-dashboard__empty">{text}</div>;
}

const FilterDropdown = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El menú abre siempre hacia abajo — si el filtro queda cerca del borde
  // inferior de la pantalla, no entra y se corta contra el viewport. Si no
  // hay lugar debajo pero sí arriba, abre para arriba en su lugar.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuMaxHeight = 340;
      setOpenUp(window.innerHeight - rect.bottom < menuMaxHeight && rect.top > menuMaxHeight);
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const hasValue = Boolean(value);

  return (
    <div className="apld-fd" ref={ref}>
      <button type="button" className="apld-fd-toggle" onClick={toggleOpen}>
        <span className={selected ? "" : "apld-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        {hasValue && (
          <span
            role="button"
            aria-label="clear"
            className="apld-fd-clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"/>
            </svg>
          </span>
        )}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`apld-fd-arrow${open ? " apld-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className={`apld-fd-menu${openUp ? " apld-fd-menu--up" : ""}`}>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`apld-fd-item${String(o.value) === String(value) ? " apld-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function AnnualPlanDashboard({ companyName, siteName } = {}) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const { site } = useSite();
  const { company } = useCompany();
  const t = (key) => translations[key] || key;

  const [year,   setYear]   = useState(CURRENT_YEAR);
  const kpiRef    = useRef();
  const tableRef  = useRef();
  const [items,  setItems]  = useState(null);
  const [reload, setReload] = useState(false);

  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  const [filterSearch,   setFilterSearch]   = useState("");
  const [filterArea,     setFilterArea]     = useState("");
  const [filterActivity, setFilterActivity] = useState("");
  const [filterStatus,   setFilterStatus]   = useState("");

  const [catAreas,      setCatAreas]      = useState([]);
  const [catActivities, setCatActivities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [ar, ac] = await Promise.allSettled([
          areaController.getAreas(accessToken),
          activityController.getActivities(accessToken),
        ]);
        if (ar.status === "fulfilled")
          setCatAreas(ar.value.map((x) => ({ key: x._id, value: x._id, text: x.name })));
        if (ac.status === "fulfilled")
          setCatActivities(ac.value.map((x) => ({ key: x._id, value: x._id, text: x.name })));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const filters = {};
        if (site) filters.site = site;
        if (company) filters.company = company;
        const res = await apController.getAnnualPlans(accessToken, filters);
        setItems(res);
      } catch {
        setItems([]);
      }
    })();
  }, [site, company, reload]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      const inYear = (() => {
        if (item.year !== undefined && item.year !== null) return item.year === year;
        if (item.fecha_programada) return new Date(item.fecha_programada).getFullYear() === year;
        return new Date(item.createdAt).getFullYear() === year;
      })();
      if (!inYear) return false;

      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const matches =
          item.area?.name?.toLowerCase().includes(q) ||
          item.actividades?.name?.toLowerCase().includes(q) ||
          item.frecuencia?.name?.toLowerCase().includes(q) ||
          item.status?.toLowerCase().includes(q) ||
          AP_STATUS_LABEL[item.status]?.toLowerCase().includes(q);
        if (!matches) return false;
      }

      const areaId = item.area?._id || item.area || "";
      if (filterArea && areaId !== filterArea) return false;

      const actId = item.actividades?._id || item.actividades || "";
      if (filterActivity && actId !== filterActivity) return false;

      if (filterStatus && item.status !== filterStatus) return false;

      return true;
    });
  }, [items, year, filterSearch, filterArea, filterActivity, filterStatus]);

  const kpis = useMemo(() => {
    if (!filteredItems.length) return { cumplimiento: 0, enTramite: 0, vencidos: 0, proximos30: 0 };
    const cumplimiento = Math.round(
      filteredItems.reduce((sum, i) => sum + (i.progress || 0), 0) / filteredItems.length
    );
    const enTramite = filteredItems.filter((i) => i.status === "in_progress").length;
    const vencidos  = filteredItems.filter((i) => i.status === "overdue").length;
    const now = new Date();
    const d30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const proximos30 = filteredItems.filter((item) =>
      (item.occurrences || []).some((o) => {
        const d = new Date(o.date);
        return !o.completed && d >= now && d <= d30;
      })
    ).length;
    return { cumplimiento, enTramite, vencidos, proximos30};
  }, [filteredItems]);

  const areaData = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      const name = item.area?.name || t("no_area");
      if (!groups[name]) groups[name] = { count: 0, progressSum: 0 };
      groups[name].count += 1;
      groups[name].progressSum += item.progress || 0;
    });
    // `avgProgress` solo se usa en el tooltip (al pasar el mouse) — el pill
    // debajo del gráfico muestra únicamente el nombre del área.
    return Object.entries(groups).map(([type, g]) => {
      const avgProgress = g.count ? Math.round(g.progressSum / g.count) : 0;
      return { type, value: g.count, avgProgress };
    });
  }, [filteredItems, t]);

  const monthData = useMemo(() => {
    const counts = Array(12).fill(0);
    filteredItems.forEach((item) => {
      const occs = item.occurrences || [];
      if (occs.length) {
        occs.forEach((o) => {
          const d = new Date(o.date);
          if (d.getFullYear() === year && !o.completed) counts[d.getMonth()]++;
        });
      } else if (item.fecha_programada) {
        const d = new Date(item.fecha_programada);
        if (d.getFullYear() === year) counts[d.getMonth()]++;
      }
    });
    return MONTH_KEYS.map((key, i) => ({ mes: t(key), value: counts[i] }));
  }, [filteredItems, year]);

  const activityData = useMemo(() => {
    const counts = {};
    filteredItems.forEach((item) => {
      const name = item.frecuencia?.name || t("no_activity");
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredItems]);

  const pieConfig = (data) => {
    // `scale.color.range` con un color por dato (DONUT_PALETTE, mismo
    // criterio que Requisitos), en el mismo orden que la leyenda.
    const colors = data.map((_, i) => DONUT_PALETTE[i % DONUT_PALETTE.length]);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: { items: [{ channel: "y", name: t("count") }] },
      height: 250,
    };
  };

  // Igual que pieConfig, pero el tooltip suma el % de avance promedio del
  // área (solo tiene sentido acá — activityData no trae `avgProgress`).
  const areaPieConfig = (data) => ({
    ...pieConfig(data),
    tooltip: {
      items: [
        (d) => ({ name: t("count"), value: d.value }),
        (d) => ({ name: t("compliance_percent"), value: `${d.avgProgress}%` }),
      ],
    },
  });

  const colConfig = {
    data: monthData,
    xField: "mes",
    yField: "value",
    colorField: "mes",
    scale: { color: { range: GREEN_PALETTE }, x: MONTH_BAR_SCALE_X },
    legend: false,
    height: 250,
    style: MONTH_BAR_STYLE,
    axis: { x: MONTH_BAR_X_AXIS, y: MONTH_BAR_Y_AXIS },
    tooltip: { items: [{ channel: "y", name: t("scheduled_by_month") }] },
    interaction: MONTH_BAR_INTERACTION,
  };

  const hasMonthData = monthData.some((d) => d.value > 0);

  // El Estado del Plan Anual se calcula (no se elige a mano): solo estos 4 valores.
  const STATUS_OPTIONS = Object.keys(AP_STATUS_LABEL).map((value) => ({ key: value, value, text: AP_STATUS_LABEL[value] }));

  const areaOptions = catAreas;

  const activityOptions = catActivities;

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const areaColorMap = Object.fromEntries(areaData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));
      const activityColorMap = Object.fromEntries(activityData.map((d, i) => [d.type, DONUT_PALETTE[i % DONUT_PALETTE.length]]));

      const [areaDonut, monthColumn, activityDonut] = await Promise.all([
        renderDonutForPdf(areaData, areaColorMap, { showPercentLabels: true }),
        renderColumnForPdf(monthData, { xField: "mes", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25 }),
        renderDonutForPdf(activityData, activityColorMap, { showPercentLabels: true }),
      ]);
      [areaDonut, monthColumn, activityDonut].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const tableColumns = [
        // Con "Total Grupo" (sin sitio puntual elegido) los datos vienen de
        // varios sitios mezclados — sin esta columna no se puede saber a
        // cuál pertenece cada fila (igual que en la tabla en pantalla).
        ...(!site ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
        { key: "area", header: t("area"), flex: 1.2, value: (r) => r.area?.name || "-" },
        { key: "activity", header: t("activity"), flex: 1.4, value: (r) => r.actividades?.name || "-" },
        { key: "frequency", header: t("frequency"), flex: 1, value: (r) => r.frecuencia?.name || "-" },
        {
          // jsPDF con fuentes estándar (Helvetica) sólo soporta WinAnsi/CP1252: la
          // flecha "→" no está en esa tabla y corrompe toda la celda al dibujarla acá.
          key: "scheduledDate", header: t("scheduled_date"), flex: 1.4,
          value: (r) => (r.occurrences?.length > 1
            ? `${formatDate(r.occurrences[0].date)} - ${formatDate(r.occurrences[r.occurrences.length - 1].date)}`
            : formatDate(r.occurrences?.[0]?.date || r.fecha_programada)),
        },
        {
          key: "status", header: t("status"), flex: 1, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            if (!row.status) return;
            doc.setFontSize(8);
            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(AP_STATUS_LABEL[row.status] || row.status, x + width / 2, y + height / 2 + 3, { align: "center" });
          },
        },
        {
          key: "progress", header: t("progress"), flex: 1.2, align: "center",
          render: (doc, { x, y, width, height, row }) => {
            doc.setFontSize(8);
            doc.setFont(undefined, "normal");
            doc.setTextColor(0, 0, 0);
            doc.text(`${row.progress || 0}%`, x + width / 2, y + height / 2 + 3, { align: "center" });
          },
        },
      ];

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("annual_plan")} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows: [
          {
            height: 210,
            cards: [
              { title: t("activity_by_area"), ...areaDonut, legendPosition: "right" },
              { title: t("annual_compliance_evolution"), ...monthColumn },
              { title: t("activity_by_frecuency"), ...activityDonut, legendPosition: "right" },
            ],
          },
        ],
        table: { columns: tableColumns, rows: filteredItems, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
        tableTitle: `${t("annual_plan")} — ${year}`,
        filename: `plan_anual_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  return (
    <div className="ap-dashboard">
      {/* ── Filtros — afuera de la card gris de KPIs, arriba de todo (igual
          que Inicio/Requisitos) */}
      <div className="ap-dashboard__header">
        <YearBar year={year} options={YEAR_OPTIONS} onChange={setYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      <div className="resumen-wrapper ap-dashboard__kpi-wrapper">
        {items === null ? (
          <LoadingSpinner />
        ) : (
          <div className="ap-dashboard__kpis" ref={kpiRef}>
            <KPICard
              label={t("activity_totals")}
              value={`${filteredItems.length}`}
            />
            <KPICard label={t("compliance_percent")}
              value={`${kpis.cumplimiento}%`}
              rawValue={kpis.cumplimiento}
              showProgress
              progressColor="blue" />
            <KPICard label={t("next_30_days")}
              value={kpis.proximos30}
              subtitle={t("pending_label")} />
            <KPICard
              label={t("status_vencido")}
              value={kpis.vencidos}
            />
          </div>
        )}
      </div>

      {items !== null && (
        <div className="ap-dashboard__charts">
          <div className="ap-dashboard__chart-slot">
            <ChartBox title={t("activity_by_area")}>
              {areaData.length > 0 ? (
                <div className="ap-dashboard__pie-row">
                  <div className="chartWrapper ap-dashboard__pie-wrapper">
                    <Pie {...areaPieConfig(areaData)} />
                  </div>
                  <ChartLegend items={areaData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
                </div>
              ) : (
                <div className="chartWrapper ap-dashboard__pie-wrapper"><div className="ap-dashboard__empty-ring" /></div>
              )}
            </ChartBox>
          </div>
          <div className="ap-dashboard__chart-slot">
            <ChartBox title={t("annual_compliance_evolution")}>
              <div className="chartWrapper">
                {hasMonthData ? <Column {...colConfig} /> : <EmptyMsg />}
              </div>
            </ChartBox>
          </div>
          <div className="ap-dashboard__chart-slot">
            <ChartBox title={t("activity_by_frecuency")}>
              {activityData.length > 0 ? (
                <div className="ap-dashboard__pie-row">
                  <div className="chartWrapper ap-dashboard__pie-wrapper">
                    <Pie {...pieConfig(activityData)} />
                  </div>
                  <ChartLegend items={activityData.map((d, i) => ({ label: d.type, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))} />
                </div>
              ) : (
                <div className="chartWrapper ap-dashboard__pie-wrapper"><div className="ap-dashboard__empty-ring" /></div>
              )}
            </ChartBox>
          </div>
        </div>
      )}

      {/* Filtros + tabla — mismo envoltorio gris que Inicio/Requisitos. */}
      {items !== null && (
        <div className="ap-dashboard__data-wrap">
          <div className="ap-dashboard__filters">
            <div className="ap-dashboard__search-wrapper">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                className="ap-dashboard__search-input"
                placeholder={`${t("search")}...`}
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </div>
            <div className="ap-dashboard__filter-select">
              <FilterDropdown options={areaOptions} value={filterArea} onChange={setFilterArea} placeholder={t("area")} />
            </div>
            <div className="ap-dashboard__filter-select">
              <FilterDropdown options={activityOptions} value={filterActivity} onChange={setFilterActivity} placeholder={t("activity")} />
            </div>
            <div className="ap-dashboard__filter-select">
              <FilterDropdown options={STATUS_OPTIONS} value={filterStatus} onChange={setFilterStatus} placeholder={t("status")} />
            </div>
          </div>

          <div className="ap-dashboard__table-section" ref={tableRef}>
            <ReadOnlyTable items={filteredItems} t={t} showSite={!site} />
          </div>

          {/* Referencias — mismo patrón que Comparativa en Inicio. */}
          <div className="ap-dashboard__legend">
            <span className="ap-dashboard__legend-title">{t("references")}</span>
            <div className="ap-dashboard__legend-row">
              {LEGEND_STEPS.map((step) => (
                <div key={step.key} className="ap-dashboard__legend-group">
                  <span className="ap-dashboard__legend-label">{t(step.labelKey)}</span>
                  <span className="ap-dashboard__legend-swatches">
                    {["blue", "green"].map((tone) => (
                      <span
                        key={tone}
                        className="ap-dashboard__legend-swatch"
                        style={{ backgroundColor: LEGEND_SWATCH_COLORS[step.key][tone] }}
                      />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
